import { useCallback, useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { useAccountId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const NotesReadStateQuery = graphql(/* GraphQL */ `
  query NotesReadState($input: CollaborationNotesReadStateInput!) {
    collaborationNotesReadState(input: $input) {
      readThroughNoteId
      firstUnreadNoteId
      unreadNoteCount
    }
  }
`)

export type NotesReadState = ResultOf<
  typeof NotesReadStateQuery
>['collaborationNotesReadState']

type ScopedState = {
  key: string
  state: NotesReadState | null
  status: 'loading' | 'ready' | 'failed'
}

export function useNotesReadState(postId: string | null): {
  status: 'idle' | ScopedState['status']
  state: NotesReadState | null
  apply: (state: NotesReadState) => void
  refresh: () => Promise<void>
  retry: () => Promise<void>
} {
  const client = useApolloClient()
  const accountId = useAccountId()
  const key = accountId && postId ? JSON.stringify([accountId, postId]) : null
  const currentKey = useRef(key)
  currentKey.current = key
  const requestVersion = useRef(0)
  const [scoped, setScoped] = useState<ScopedState | null>(null)

  const apply = useCallback(
    (state: NotesReadState): void => {
      if (!key || currentKey.current !== key) return
      requestVersion.current += 1
      setScoped({ key, state, status: 'ready' })
    },
    [key],
  )

  const refresh = useCallback(async (): Promise<void> => {
    if (!key || !postId) return
    const version = ++requestVersion.current
    try {
      const response = await client.query({
        query: NotesReadStateQuery,
        variables: { input: { postId } },
        fetchPolicy: 'no-cache',
        context: { queryDeduplication: false },
      })
      if (currentKey.current !== key || version !== requestVersion.current)
        return
      const state = response.data?.collaborationNotesReadState
      if (!state || response.error) throw new Error('Read state unavailable')
      setScoped({ key, state, status: 'ready' })
    } catch {
      if (currentKey.current !== key || version !== requestVersion.current)
        return
      setScoped((previous) => ({
        key,
        state: previous?.key === key ? previous.state : null,
        status: 'failed',
      }))
    }
  }, [client, key, postId])

  useEffect(() => {
    if (!key) return
    setScoped({ key, state: null, status: 'loading' })
    void refresh()
    return (): void => {
      requestVersion.current += 1
    }
  }, [key, refresh])

  return {
    status: !key ? 'idle' : scoped?.key === key ? scoped.status : 'loading',
    state: scoped?.key === key ? scoped.state : null,
    apply,
    refresh,
    retry: refresh,
  }
}
